import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { stringify, parse } from 'querystringify'

// Actions
import { actions as browseActions } from 'modules/browse/actions'
import { actions as collectionsActions } from 'modules/collections/actions'

// Components
import { Error, Layout, Link, Spinner, IconPen, IconRemove } from '@tracklib/kit'
import { ContentHeader } from 'kit'
import SEO from 'components/base/seo'

import Cover from 'components/collection/cover'
import Filter from 'components/filter'
import TrackList from 'components/browse/tracklist'

// Hooks
import { useCollection } from 'modules/collections/hooks'
import { useIsAuthenticated } from 'modules/auth/hooks'
import { useProfile } from 'modules/profile/hooks'
import { useRequest } from 'modules/requests/hooks'
import { useSearchQuery } from 'modules/browse/hooks'

// Selectors
import { selectPurgedSearchString } from 'modules/browse/selectors'

//  Utils
import { isBrowser } from '@process'

// Types
import { Dispatch, QueryObject } from 'types'

export const createSearchQuery = (queryObj: QueryObject, search: string) => {
  return selectPurgedSearchString(
    `${stringify({
      ...queryObj,
      ...parse(selectPurgedSearchString(search)),
    })}`,
  )
}

export const loadInitialData =
  ({
    location: {
      params: { id },
    },
  }: {
    location: { params: { id: string } }
  }) =>
  async (dispatch: Dispatch) => {
    const searchQuery = createSearchQuery({ collection: id, limit: 10000 }, '')
    return Promise.all([dispatch(browseActions.fetchTracksBySearchQuery(searchQuery)), dispatch(collectionsActions.fetchCollection(+id))])
  }

const Collection = () => {
  const { id = '0' } = useParams() as { id?: string }
  const dispatch = useDispatch()
  const profile = useProfile()
  const isAuthenticated = useIsAuthenticated()
  const playCollectionRequest = useRequest('PREPARE_PLAY_COLLECTION')
  const fetchOwnCollectionsRequest = useRequest('FETCH_COLLECTIONS', `?owner=${profile.user}`)
  const { collection, request: fetchCollectionRequest } = useCollection(+id)
  const title = `${collection?.title}, a collection by ${collection?.owner_name} on Tracklib`
  const breadcrumbs = [
    {
      text: 'Collections',
      link: '/collections/',
    },
    {
      text: collection?.owner_name,
      link: `/user/${collection?.owner}/`,
    },
    {
      text: collection?.title,
      link: `/collections/${collection?.id}/`,
    },
  ]
  const queryObj = { collection: id, limit: 10000 }
  // NOTE(Kitti): Following line disabled to remove loadMore from trackListProps as it isnt used on collection page
  const { request, loadMore, ...trackListProps } = useSearchQuery(queryObj, collection?.tracks?.length) // eslint-disable-line
  const tracks = collection?.tracks.filter(track => trackListProps.trackIds.some(({ id: i }) => i === track.id))
  if (!collection) {
    if (fetchCollectionRequest.isDone && (!isAuthenticated || fetchOwnCollectionsRequest.isDone)) {
      return <Error message="Collection couldn't be found" />
    }
    return (
      <Layout.FullWidth>
        <SEO title="Collection | Tracklib" />
        <Spinner minHeight={400} delay={0} />
      </Layout.FullWidth>
    )
  }

  const deleteCollection = () => {
    if (window.confirm('Are you sure you want to remove the collection?')) {
      dispatch(collectionsActions.deleteCollection(collection.id, '/inspiration/'))
    }
  }

  const isOwned = collection.owner === profile.user
  const dropdownLinks = [
    ...(isOwned
      ? [
          { name: 'Edit', icon: <IconPen />, to: 'collection', modal: true, query: { collectionId: collection.id } },
          { name: 'Remove', icon: <IconRemove />, onClick: deleteCollection },
        ]
      : []),
  ]

  const type = `${collection.public ? '' : 'private'} ${collection.loop_collection ? 'loop' : ''} collection`

  return (
    <>
      <SEO title={title} description={collection.description} />
      <Layout.FullWidth>
        <ContentHeader
          playAll={() => dispatch(collectionsActions.playCollection(collection.id))}
          playAllRequest={playCollectionRequest}
          image={<Cover collection={collection} />}
          type={type}
          shortType="collection"
          title={collection.title}
          description={collection.description}
          sharePath={collection.public ? `/collection/${+id}` : undefined}
          adminEditPath={`/core/collection/${+id}/change`}
          breadcrumbs={breadcrumbs}
          by={<Link to={`/user/${collection.owner}/`}>{collection.owner_name}</Link>}
          dropdownLinks={dropdownLinks}
          overflowVisible
        />
        <Filter queryObj={queryObj} />
        {!collection && fetchCollectionRequest?.isDone && isBrowser ? (
          <Error message="Collection couldn't be found" />
        ) : (
          <TrackList {...trackListProps} totalTrackCount={tracks.length} trackIds={tracks} request={request} renderSpecificTrackNames />
        )}
      </Layout.FullWidth>
    </>
  )
}

export default Collection
